// src/utils/statisticsUtils.js
import dayjs from "@/plugins/dayjs";
import { formatTimeFromMs, normalizeDateRange } from "@/utils/timeFormatUtils";
import { getDateRangeForPeriod } from "@/utils/firestoreUtils";

export const LUNCH_PROJECT_ID = "SYSTEM_LUNCH-BREAK";

/**
 * @typedef {Object} StatisticsEntryRow
 * @property {string} entryId
 * @property {string} userId
 * @property {string} userName
 * @property {string} projectId
 * @property {string} projectName
 * @property {number} startTime
 * @property {number} endTime
 * @property {number} durationMs
 * @property {number} overtimeMs
 * @property {string|null} description
 */

/**
 * Flatten merged time-entry maps into row objects.
 *
 * @param {Object<string, Object>} mergedByUser - { userId: { entryId: entry } }
 * @param {Object} options
 * @param {Object<string, string>} [options.userNames] - userId → display name
 * @param {Object<string, string>} [options.projectNames] - projectId → name
 * @param {string[]} [options.userIds] - optional user filter
 * @param {string[]} [options.projectIds] - optional project filter
 * @param {{startDate:number, endDate:number}|null} [options.dateRange] - filter by startTime
 * @param {boolean} [options.includeLunch=false]
 * @returns {StatisticsEntryRow[]}
 */
export function flattenTimeEntries(mergedByUser = {}, options = {}) {
    const {
        userNames = {},
        projectNames = {},
        userIds = null,
        projectIds = null,
        dateRange = null,
        includeLunch = false,
    } = options;

    // null = no filter; [] = empty scope (match nothing). Do not treat [] as "all".
    const userIdSet = userIds == null ? null : new Set(userIds);
    const projectIdSet = projectIds == null ? null : new Set(projectIds);

    let rangeStart = null;
    let rangeEnd = null;
    if (dateRange?.startDate != null && dateRange?.endDate != null) {
        const normalized = normalizeDateRange(
            dateRange.startDate,
            dateRange.endDate
        );
        if (normalized) {
            rangeStart = normalized.start;
            rangeEnd = normalized.end;
        }
    }

    const rows = [];

    Object.entries(mergedByUser || {}).forEach(([userId, entriesMap]) => {
        if (userIdSet && !userIdSet.has(userId)) return;

        const entries =
            entriesMap && typeof entriesMap === "object"
                ? Object.entries(entriesMap)
                : [];

        entries.forEach(([entryId, entry]) => {
            if (!entry || entry.endTime == null || !entry.startTime) return;
            if (!includeLunch && entry.projectId === LUNCH_PROJECT_ID) return;
            if (projectIdSet && !projectIdSet.has(entry.projectId)) return;

            const start = Number(entry.startTime);
            const end = Number(entry.endTime);
            if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start)
                return;

            if (rangeStart != null && rangeEnd != null) {
                if (start < rangeStart || start > rangeEnd) return;
            }

            const durationMs = end - start;
            const overtimeMs = entry.overtime === true ? durationMs : 0;

            rows.push({
                entryId,
                userId,
                userName: userNames[userId] || userId,
                projectId: entry.projectId,
                projectName:
                    projectNames[entry.projectId] || entry.projectId,
                startTime: start,
                endTime: end,
                durationMs,
                overtimeMs,
                description: entry.description || null,
            });
        });
    });

    return rows;
}

/**
 * Aggregate KPI totals from entry rows.
 * @param {StatisticsEntryRow[]} rows
 */
export function aggregateTotals(rows = []) {
    const users = new Set();
    const projects = new Set();
    let totalMs = 0;
    let overtimeMs = 0;

    rows.forEach((row) => {
        totalMs += row.durationMs;
        overtimeMs += row.overtimeMs;
        users.add(row.userId);
        projects.add(row.projectId);
    });

    return {
        totalMs,
        overtimeMs,
        entryCount: rows.length,
        uniqueUsers: users.size,
        uniqueProjects: projects.size,
        totalFormatted: formatTimeFromMs(totalMs),
        overtimeFormatted: formatTimeFromMs(overtimeMs),
    };
}

/**
 * Group rows by project, sorted by duration desc.
 * @param {StatisticsEntryRow[]} rows
 */
export function aggregateByProject(rows = []) {
    const map = {};
    rows.forEach((row) => {
        if (!map[row.projectId]) {
            map[row.projectId] = {
                projectId: row.projectId,
                projectName: row.projectName,
                totalMs: 0,
                overtimeMs: 0,
                entries: 0,
                userNamesById: new Map(),
            };
        }
        const bucket = map[row.projectId];
        bucket.totalMs += row.durationMs;
        bucket.overtimeMs += row.overtimeMs;
        bucket.entries += 1;
        bucket.userNamesById.set(row.userId, row.userName);
    });

    return Object.values(map)
        .map((b) => {
            const userNames = [...b.userNamesById.values()].sort((a, c) =>
                a.localeCompare(c)
            );
            return {
                projectId: b.projectId,
                projectName: b.projectName,
                totalMs: b.totalMs,
                overtimeMs: b.overtimeMs,
                entries: b.entries,
                users: userNames.length,
                userNames,
                totalFormatted: formatTimeFromMs(b.totalMs),
                overtimeFormatted: formatTimeFromMs(b.overtimeMs),
            };
        })
        .sort((a, b) => b.totalMs - a.totalMs);
}

/**
 * Group rows by user, sorted by duration desc.
 * @param {StatisticsEntryRow[]} rows
 */
export function aggregateByUser(rows = []) {
    const map = {};
    rows.forEach((row) => {
        if (!map[row.userId]) {
            map[row.userId] = {
                userId: row.userId,
                userName: row.userName,
                totalMs: 0,
                overtimeMs: 0,
                entries: 0,
                projectNamesById: new Map(),
            };
        }
        const bucket = map[row.userId];
        bucket.totalMs += row.durationMs;
        bucket.overtimeMs += row.overtimeMs;
        bucket.entries += 1;
        bucket.projectNamesById.set(row.projectId, row.projectName);
    });

    return Object.values(map)
        .map((b) => {
            const projectNames = [
                ...b.projectNamesById.values(),
            ].sort((a, c) => a.localeCompare(c));
            return {
                userId: b.userId,
                userName: b.userName,
                totalMs: b.totalMs,
                overtimeMs: b.overtimeMs,
                entries: b.entries,
                projects: projectNames.length,
                projectNames,
                totalFormatted: formatTimeFromMs(b.totalMs),
                overtimeFormatted: formatTimeFromMs(b.overtimeMs),
            };
        })
        .sort((a, b) => b.totalMs - a.totalMs);
}

/**
 * Daily series keyed by YYYY-MM-DD (UTC day of startTime).
 * @param {StatisticsEntryRow[]} rows
 * @returns {Array<{ date: string, totalMs: number, overtimeMs: number, hours: number }>}
 */
export function aggregateByDay(rows = []) {
    const map = {};
    rows.forEach((row) => {
        const date = dayjs(row.startTime).utc().format("YYYY-MM-DD");
        if (!map[date]) {
            map[date] = { date, totalMs: 0, overtimeMs: 0 };
        }
        map[date].totalMs += row.durationMs;
        map[date].overtimeMs += row.overtimeMs;
    });

    return Object.values(map)
        .map((d) => ({
            ...d,
            hours: Math.round((d.totalMs / 3600000) * 100) / 100,
            totalFormatted: formatTimeFromMs(d.totalMs),
        }))
        .sort((a, b) => a.date.localeCompare(b.date));
}

/**
 * User × project matrix rows.
 * @param {StatisticsEntryRow[]} rows
 */
export function aggregateUserProjectMatrix(rows = []) {
    const map = {};
    rows.forEach((row) => {
        const key = `${row.userId}::${row.projectId}`;
        if (!map[key]) {
            map[key] = {
                userId: row.userId,
                userName: row.userName,
                projectId: row.projectId,
                projectName: row.projectName,
                totalMs: 0,
                overtimeMs: 0,
                entries: 0,
            };
        }
        map[key].totalMs += row.durationMs;
        map[key].overtimeMs += row.overtimeMs;
        map[key].entries += 1;
    });

    return Object.values(map)
        .map((b) => ({
            ...b,
            totalFormatted: formatTimeFromMs(b.totalMs),
            overtimeFormatted: formatTimeFromMs(b.overtimeMs),
        }))
        .sort(
            (a, b) =>
                a.userName.localeCompare(b.userName) ||
                b.totalMs - a.totalMs
        );
}

/**
 * Prior period of equal length immediately before [startDate, endDate].
 * @param {{startDate:number, endDate:number}} dateRange
 * @returns {{startDate:number, endDate:number}|null}
 */
export function getPriorPeriodRange(dateRange) {
    if (!dateRange?.startDate || !dateRange?.endDate) return null;
    const normalized = normalizeDateRange(
        dateRange.startDate,
        dateRange.endDate
    );
    if (!normalized) return null;

    const length = normalized.end - normalized.start;
    if (length < 0) return null;

    return {
        startDate: normalized.start - length - 1,
        endDate: normalized.start - 1,
    };
}

function formatPriorRangeLabel(range) {
    if (!range?.startDate || !range?.endDate) return "previous period";
    const start = dayjs(range.startDate).format("DD/MM/YYYY");
    const end = dayjs(range.endDate).format("DD/MM/YYYY");
    return `${start} - ${end}`;
}

function toFetchResult(range) {
    if (!range) {
        return {
            range: null,
            label: "previous period",
            fetchPeriodKey: "custom",
            fetchCustomRange: null,
        };
    }
    return {
        range,
        label: formatPriorRangeLabel(range),
        fetchPeriodKey: "custom",
        fetchCustomRange: [
            new Date(range.startDate),
            new Date(range.endDate),
        ],
    };
}

/**
 * If [start, end] is whole calendar months (1st → last day), shift back by that
 * many months. Uses dayjs month math — no per-month day counts needed.
 * Example: 01/04/2026–31/07/2026 (4 months) → 01/12/2025–31/03/2026.
 *
 * @param {{startDate:number, endDate:number}} currentRange
 * @returns {{startDate:number, endDate:number}|null}
 */
export function getCalendarMonthShiftedPriorRange(currentRange) {
    if (!currentRange?.startDate || !currentRange?.endDate) return null;

    const start = dayjs(currentRange.startDate);
    const end = dayjs(currentRange.endDate);

    const isMonthStart = start.isSame(start.startOf("month"), "day");
    const isMonthEnd = end.isSame(end.endOf("month"), "day");
    if (!isMonthStart || !isMonthEnd) return null;

    const monthCount =
        end.startOf("month").diff(start.startOf("month"), "month") + 1;
    if (monthCount < 1) return null;

    const priorStart = start.subtract(monthCount, "month").startOf("month");
    const priorEnd = end.subtract(monthCount, "month").endOf("month");

    return {
        startDate: priorStart.startOf("day").utc().valueOf(),
        endDate: priorEnd.endOf("day").utc().valueOf(),
    };
}

/**
 * Resolve the comparison window for KPI deltas.
 * Named presets use calendar priors (This Month → Last Month full month).
 * Custom ranges that are whole calendar months shift by that many months.
 * Otherwise falls back to an equal-length millisecond window.
 *
 * @param {string} periodKey
 * @param {Array<Date>|null} customDateRange
 * @returns {{
 *   range: {startDate:number, endDate:number}|null,
 *   label: string,
 *   fetchPeriodKey: string,
 *   fetchCustomRange: Array<Date>|null
 * }}
 */
export function resolvePriorComparison(periodKey, customDateRange = null) {
    const semantic = {
        today: { key: "yesterday", label: "Yesterday" },
        thisWeek: { key: "lastWeek", label: "Last Week" },
        thisMonth: { key: "lastMonth", label: "Last Month" },
    };

    if (semantic[periodKey]) {
        const { key, label } = semantic[periodKey];
        return {
            range: getDateRangeForPeriod(key),
            label,
            fetchPeriodKey: key,
            fetchCustomRange: null,
        };
    }

    // Calendar month before "Last Month"
    if (periodKey === "lastMonth") {
        const twoMonthsAgo = dayjs().subtract(2, "month");
        return toFetchResult({
            startDate: twoMonthsAgo.startOf("month").utc().valueOf(),
            endDate: twoMonthsAgo.endOf("month").utc().valueOf(),
        });
    }

    try {
        const current = getDateRangeForPeriod(periodKey, customDateRange);

        // Custom (or any) whole-month spans: shift by calendar months
        const monthShifted = getCalendarMonthShiftedPriorRange(current);
        if (monthShifted) {
            return toFetchResult(monthShifted);
        }

        // Arbitrary mid-month ranges: equal-length window
        return toFetchResult(getPriorPeriodRange(current));
    } catch {
        return toFetchResult(null);
    }
}

/**
 * Friendly label for the prior comparison window.
 * @param {string} periodKey
 * @param {Array<Date>|null} customDateRange
 * @returns {string}
 */
export function getPriorPeriodLabel(periodKey, customDateRange = null) {
    return resolvePriorComparison(periodKey, customDateRange).label;
}

/**
 * Percent change from previous to current. Null if previous is 0 and current is 0.
 * @param {number} current
 * @param {number} previous
 * @returns {number|null}
 */
export function percentChange(current, previous) {
    if (previous === 0) {
        if (current === 0) return 0;
        return null;
    }
    return Math.round(((current - previous) / previous) * 1000) / 10;
}

/**
 * Build KPI comparison object.
 * @param {ReturnType<typeof aggregateTotals>} current
 * @param {ReturnType<typeof aggregateTotals>} previous
 */
export function buildKpiComparison(current, previous) {
    return {
        totalMs: current.totalMs,
        overtimeMs: current.overtimeMs,
        uniqueUsers: current.uniqueUsers,
        uniqueProjects: current.uniqueProjects,
        entryCount: current.entryCount,
        totalFormatted: current.totalFormatted,
        overtimeFormatted: current.overtimeFormatted,
        totalChange: percentChange(current.totalMs, previous.totalMs),
        overtimeChange: percentChange(current.overtimeMs, previous.overtimeMs),
        usersChange: percentChange(current.uniqueUsers, previous.uniqueUsers),
        projectsChange: percentChange(
            current.uniqueProjects,
            previous.uniqueProjects
        ),
    };
}

/**
 * Format a single description line like Daily Report: "text (Xh:Ymin)" / "text (Ym)".
 * @param {string} description
 * @param {number} durationMs
 * @returns {string|null}
 */
export function formatDescriptionWithDuration(description, durationMs) {
    if (!description) return null;
    const mins = durationMs / 60000;
    const h = Math.floor(mins / 60);
    const m = Math.round(mins % 60);
    const label = h ? `${h}h:${m}min` : `${m}min`;
    return `${description} (${label})`;
}

/**
 * Like formatDescriptionWithDuration, optionally inserting project name before duration:
 * `Worked on API (Korche Int) (1h:0min)`.
 *
 * @param {string} description
 * @param {string|null|undefined} projectName
 * @param {number} durationMs
 * @returns {string|null}
 */
export function formatDescriptionWithProjectName(
    description,
    projectName,
    durationMs
) {
    if (!description) return null;
    const name = projectName && String(projectName).trim();
    const withName = name ? `${description} (${name})` : description;
    return formatDescriptionWithDuration(withName, durationMs);
}

export const TIMESHEET_EXPORT_COLUMNS = [
    "Date",
    "Employee",
    "Project",
    "Total Time",
    "Time (Hours)",
    "Description",
];

/**
 * Convert milliseconds to decimal hours (2 places), e.g. 8.21.
 * @param {number} ms
 * @returns {number}
 */
export function msToDecimalHours(ms) {
    return Math.round(((ms || 0) / 3600000) * 100) / 100;
}

/**
 * Timesheet export rows: one per employee + project + calendar day.
 * Descriptions stack like Daily Report (newline-separated).
 * Total Time = "Xh:Ym"; Time (Hours) = decimal hours.
 *
 * @param {StatisticsEntryRow[]} rows
 * @returns {Array<Object>}
 */
export function buildTimesheetExportRows(rows = []) {
    const map = {};

    rows.forEach((row) => {
        const dateKey = dayjs(row.startTime).format("MM/DD/YYYY");
        const key = `${dateKey}|${row.userId}|${row.projectId}`;
        if (!map[key]) {
            map[key] = {
                Date: dateKey,
                Employee: row.userName,
                Project: row.projectName,
                totalMs: 0,
                descriptions: [],
                sortTs: dayjs(row.startTime).startOf("day").valueOf(),
            };
        }
        map[key].totalMs += row.durationMs;
        const descLine = formatDescriptionWithDuration(
            row.description,
            row.durationMs
        );
        if (descLine) {
            map[key].descriptions.push(descLine);
        }
    });

    return Object.values(map)
        .map((b) => ({
            Date: b.Date,
            Employee: b.Employee,
            Project: b.Project,
            "Total Time": formatTimeFromMs(b.totalMs),
            "Time (Hours)": msToDecimalHours(b.totalMs),
            Description: b.descriptions.join("\n"),
            _sortTs: b.sortTs,
            _employee: b.Employee,
            _project: b.Project,
        }))
        .sort(
            (a, b) =>
                a._sortTs - b._sortTs ||
                a._employee.localeCompare(b._employee) ||
                a._project.localeCompare(b._project)
        )
        .map(({ _sortTs, _employee, _project, ...row }) => row);
}
